import {useTranslation} from "react-i18next";
import {useToast} from "../../modules/layout/components/ToastContext";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import React, {useEffect, useState} from "react";
import {Field, Form, Formik} from "formik";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import {IConsumptionLog, IFilterConsumptionLogModel} from "../../interfaces/IUtilities";
import {NavLink, useNavigate} from "react-router-dom";
import {getAll} from "../_request";
import Loader from "../../modules/layout/components/Loader";
import {FormikDatePicker} from "../../modules/layout/components/FormikDatePicker";
import Pagination from "../../modules/layout/components/Pagination";
import {formatDateTime, toApiDateTime} from "../../helpers/DateTimeHelper";

const initialFormState: IFilterConsumptionLogModel = {
    datefrom: '',
    dateto: '',
    spokeName: '',
    className: '',
    consentId: '',
    subjectId: '',
    backendStatusCode: '',
    errorCode: '',
    errorText: '',
    correlationId: ''
};

const ConsumptionLogList = () => {
    const {t} = useTranslation();
    const {showToast} = useToast();
    const navigate = useNavigate();

    const [logs, setLogs] = useState<IConsumptionLog[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [activeFilters, setActiveFilters] = useState<IFilterConsumptionLogModel>(initialFormState);

    useEffect(() => {
        fetchLogs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);

    const handleFilterSearch = async (filters: IFilterConsumptionLogModel) => {
        setPage(1);
        setActiveFilters(filters);
        await fetchLogs(filters);
    };

    const fetchLogs = async (filters: IFilterConsumptionLogModel = activeFilters) => {
        try {
            const params: { key: string; value: string | number }[] = [
                { key: 'page', value: page },
                { key: 'pageSize', value: pageSize }
            ];
            if (filters.datefrom) params.push({ key: 'dateFrom', value: toApiDateTime(filters.datefrom)! });
            if (filters.dateto)   params.push({ key: 'dateTo',   value: toApiDateTime(filters.dateto, true)! });
            if (filters.spokeName) params.push({ key: 'spokeName', value: filters.spokeName });
            if (filters.className)   params.push({ key: 'className',   value: filters.className });
            if (filters.consentId)   params.push({ key: 'consentId',   value: filters.consentId });
            if (filters.subjectId)   params.push({ key: 'subjectId',   value: filters.subjectId });
            if (filters.backendStatusCode) params.push({ key: 'backendStatusCode', value: filters.backendStatusCode });
            if (filters.errorCode)   params.push({ key: 'errorCode',   value: filters.errorCode });
            if (filters.errorText)   params.push({ key: 'errorText',   value: filters.errorText });
            if (filters.correlationId) params.push({ key: 'correlationId', value: filters.correlationId });

            const query = '?' + params
                .map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(String(p.value))}`)
                .join('&');

            const result = await getAll('/api/consumptionlogs/getall', query);
            const respStatus = result?.status || result?.response?.status;

            if (respStatus === 200) {
                const items = result?.data?.items;
                setLogs(Array.isArray(items) ? items as IConsumptionLog[] : []);
                setTotal(typeof result?.data?.totalCount === 'number' ? result.data.totalCount : 0);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', {code: String(respStatus)}), 'error');
            }
        } catch (error) {
            setLoading(false);
            showToast(t('ConnectionError', {error: String(error)}), 'error');
        }
    };

    return (
        <div className="gg-consumptionLogList container mt-4">
            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('ConsumptionLogList')}
                ]}/>

            <h3 className="mb-3">{t('ConsumptionLogList')}</h3>

            <FiltersCard>
                <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({resetForm, values}) => {
                        const today = new Date();
                        const fromVal = values.datefrom ? new Date(values.datefrom) : null;
                        const toVal = values.dateto ? new Date(values.dateto) : null;
                        return (
                        <Form>
                            <div className="row">
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('DateFrom')}
                                        <FormikDatePicker name="datefrom" showTime placeholderText={t('ChooseDate')}
                                                          maxDate={toVal && toVal < today ? toVal : today}/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('DateTo')}
                                        <FormikDatePicker name="dateto" showTime endOfDayDefault placeholderText={t('ChooseDate')}
                                                          minDate={fromVal}
                                                          maxDate={today}/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('SpokeName')}
                                        <Field className="form-control" name="spokeName"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('ClassName')}
                                        <Field className="form-control" name="className"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('ConsentId')}
                                        <Field className="form-control" name="consentId"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('SubjectId')}
                                        <Field className="form-control" name="subjectId"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('BackendStatusCode')}
                                        <Field className="form-control" name="backendStatusCode" type="number"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('ErrorCode')}
                                        <Field className="form-control" name="errorCode"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('ErrorText')}
                                        <Field className="form-control" name="errorText"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('CorrelationId')}
                                        <Field className="form-control" name="correlationId"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3 align-self-end">
                                    <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                                        {t('Search')}
                                    </button>
                                    <button type="button" className="btn btn-secondary text-white mb-2"
                                            onClick={() => {
                                                resetForm();
                                                setActiveFilters(initialFormState);
                                                handleFilterSearch(initialFormState);
                                            }}>
                                        {t('Reset')}
                                    </button>
                                </div>
                            </div>
                        </Form>
                        );
                    }}
                </Formik>
            </FiltersCard>

            {loading && (<Loader/>)}

            <div className="table-responsive border p-1 rounded-2">
                <table className="table table-sm table-striped-columns small table-logs">
                    <thead>
                    <tr>
                        <th className="text-nowrap">{t('RequestTimestamp')}</th>
                        <th className="text-nowrap">{t('SpokeName')}</th>
                        <th className="text-nowrap">{t('ClassName')}</th>
                        <th className="text-nowrap">{t('ConsentId')}</th>
                        <th className="text-nowrap">{t('SubjectId')}</th>
                        <th className="text-nowrap">{t('BackendStatusCode')}</th>
                        <th className="text-nowrap">{t('ResponseTimeMs')}</th>
                        <th className="text-nowrap">{t('ErrorCode')}</th>
                    </tr>
                    </thead>
                    <tbody className="table-group-divider">
                    {logs.map(log => (
                        <tr key={log.id} className={'table-logs__item ' + (log.errorCode ? 'table-logs__item--error' : '')}>
                            <td className="text-nowrap">
                                <NavLink to={'/consumption-logs/details/' + log.id}>
                                    {formatDateTime(log.requestTimestamp)}
                                </NavLink>
                            </td>
                            <td className="text-nowrap">{log.spokeName || log.spokeUserId}</td>
                            <td className="text-nowrap">{log.className}</td>
                            <td className="text-nowrap" title={log.consentId}>{log.consentId?.slice(0, 8)}…</td>
                            <td className="text-nowrap">{log.subjectId}</td>
                            <td className="text-nowrap">{log.backendStatusCode ?? '—'}</td>
                            <td className="text-nowrap">{log.responseTimeMs ?? '—'}</td>
                            <td className="text-nowrap">{log.errorCode ?? ''}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <Pagination currentPage={page}
                        onPageChange={(p) => setPage(p)}
                        pageSize={pageSize}
                        onPageSizeChange={(s) => setPageSize(s)}
                        totalItems={total}/>
        </div>
    );
};

export default ConsumptionLogList;
